import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import { useGetAppSettingsQuery } from '../../store/siteContentApi';

// Fallbacks mirror the original hardcoded values so the section still
// renders sensibly if the admin hasn't saved settings yet, or the fetch
// hasn't resolved on first paint.
const FALLBACK = {
  // Real listings, verified against Apple's lookup API and the Play
  // Store package name. The previous values here were placeholders from
  // before either app was published and resolved to nothing.
  ios_url: 'https://apps.apple.com/in/app/trustlab/id6792648826',
  android_url: 'https://play.google.com/store/apps/details?id=com.trustlab.app',
  rating: '4.5 ★',
  downloads: '1M+',
  tests_available: '1700+',
};

const AppDownload = () => {
  const { data: settings } = useGetAppSettingsQuery();
  const s = {
    ios_url:         settings?.ios_url         || FALLBACK.ios_url,
    android_url:     settings?.android_url     || FALLBACK.android_url,
    rating:          settings?.rating          || FALLBACK.rating,
    downloads:       settings?.downloads       || FALLBACK.downloads,
    tests_available: settings?.tests_available || FALLBACK.tests_available,
  };
  const stats = [
    { value: s.rating,          label: 'App Store Rating' },
    { value: s.downloads,       label: 'Downloads' },
    { value: s.tests_available, label: 'Tests Available' },
  ];

  return (
  <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', py: { xs: 7, md: 10 } }}>
    <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' },
          gap: { xs: 5, md: 8 },
          alignItems: 'center',
        }}
      >
        {/* LEFT */}
        <Box>
          {/* Eyebrow */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 2,
              lineHeight: 1,
            }}
          >
            <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage-l)' }} />
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--sage-l)',
                lineHeight: 1,
              }}
            >
              Mobile App
            </Typography>
          </Box>

          <Typography
            className="serif"
            sx={{
              fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
              fontWeight: 500,
              color: '#fff',
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            Download the TrustLab App
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 13, md: 14 },
              color: 'rgba(255,255,255,0.45)',
              maxWidth: 420,
              mb: 3,
              fontWeight: 300,
              lineHeight: 1.7,
            }}
          >
            Book tests, track your Trustian live, view all reports and monitor your health trends — all in one app.
          </Typography>

          {/* Store buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              href={s.ios_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.125,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px',
                px: 2,
                py: 1.25,
                color: '#fff',
                textTransform: 'none',
                textAlign: 'left',
                lineHeight: 1,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <AppleIcon sx={{ fontSize: 24, color: '#E53E3E', display: 'block' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
                <Typography
                  component="span"
                  sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 400, lineHeight: 1 }}
                >
                  Download on the
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1 }}
                >
                  App Store
                </Typography>
              </Box>
            </Button>

            <Button
              href={s.android_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.125,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '12px',
                px: 2,
                py: 1.25,
                color: '#fff',
                textTransform: 'none',
                textAlign: 'left',
                lineHeight: 1,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <AndroidIcon sx={{ fontSize: 24, color: '#A4C639', display: 'block' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
                <Typography
                  component="span"
                  sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 400, lineHeight: 1 }}
                >
                  Get it on
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1 }}
                >
                  Google Play
                </Typography>
              </Box>
            </Button>
          </Box>
        </Box>

        {/* RIGHT — 3 stat cards stacked */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '100%',
            maxWidth: { xs: '100%', md: 260 },
            ml: { md: 'auto' },
          }}
        >
          {stats.map((stat) => (
            <Box
              key={stat.label}
              sx={{
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                px: 3,
                py: 2.25,
                textAlign: 'center',
              }}
            >
              <Typography
                className="serif"
                sx={{
                  fontSize: { xs: 24, md: 30 },
                  fontWeight: 600,
                  color: 'var(--sage-l)',
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.4)',
                  mt: 0.75,
                  lineHeight: 1,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  </Box>
  );
};

export default AppDownload;
